import { Mail, Image as ImageIcon, Clock, Tag, Rocket, Users, Lightbulb, Settings } from "lucide-react";
import type { ArticleContent, ArticleTile } from "./types";
import { Hero, Section, Steps, Bullets, Tip } from "./renderHelpers";

const CATEGORY = "Getting Started";

export const tiles: ArticleTile[] = [
  { slug: "inviting-members", title: "Inviting Members & Sending Invitations", category: CATEGORY, description: "Send invites and manage pending members", icon: Mail },
  { slug: "pta-branding-logo", title: "Setting Up Your PTA Branding & Logo", category: CATEGORY, description: "Upload your logo and customise branding", icon: ImageIcon },
  { slug: "free-trial-explained", title: "Understanding the 28-Day Free Trial", category: CATEGORY, description: "What's included and what happens after", icon: Clock },
  { slug: "promo-codes", title: "Using Promo Codes to Extend Your Trial", category: CATEGORY, description: "Apply codes to extend your free period", icon: Tag },
];

export const articles: Record<string, ArticleContent> = {
  "inviting-members": {
    title: "Inviting Members & Sending Invitations",
    category: CATEGORY,
    readTime: "4 min read",
    content: (
      <div className="space-y-6">
        <Hero>Grow your PTA committee by inviting members directly from the Members page. They receive a branded email and follow a simple link to join.</Hero>
        <Section icon={Rocket} title="How to send an invitation">
          <Steps items={[
            <>Go to <strong>Members</strong> from the sidebar.</>,
            <>Click <strong>Invite Member</strong> in the top right.</>,
            <>Enter their first name, last name and email address.</>,
            <>Optionally pre-assign a role (e.g. Treasurer, Secretary).</>,
            <>Click <strong>Send Invitation</strong>. They receive an email with an Accept Invitation link.</>,
          ]} />
        </Section>
        <Section icon={Users} title="What happens next">
          <Bullets items={[
            "The invitee clicks the link and creates a password to join your PTA.",
            "They appear in your Members list automatically with the role you pre-selected.",
            "Pending invites stay visible until accepted, expired, or revoked.",
          ]} />
        </Section>
        <Tip>Roles requiring a paid add-on (e.g. Catering) only show up if your subscription includes them.</Tip>
      </div>
    ),
  },
  "pta-branding-logo": {
    title: "Setting Up Your PTA Branding & Logo",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Your PTA logo appears in the header, on emails, invoices and on your public fundraising page. Upload it once in Settings → General.</Hero>
        <Section icon={Settings} title="Upload your logo">
          <Steps items={[
            <>Open <strong>Settings → General</strong>.</>,
            <>Scroll to <strong>PTA Logo</strong> and click <strong>Upload</strong>.</>,
            <>Choose a square PNG or JPG (under 10MB) — transparent backgrounds look best.</>,
            <>Save. The logo updates immediately across the platform.</>,
          ]} />
        </Section>
        <Section icon={Lightbulb} title="Where it appears">
          <Bullets items={[
            "Top-left of your admin header (replaces the PTA name when set).",
            "Public fundraising page header.",
            "Outgoing branded emails to parents and members.",
            "Generated invoices, receipts and event reports.",
          ]} />
        </Section>
        <Tip>If no logo is uploaded, your PTA name shows in plain text. There's no need to set a logo elsewhere — General is the single source of truth.</Tip>
      </div>
    ),
  },
  "free-trial-explained": {
    title: "Understanding the 28-Day Free Trial",
    category: CATEGORY,
    readTime: "3 min read",
    content: (
      <div className="space-y-6">
        <Hero>Every new PTA gets a 28-day free trial of the Standard plan, no card required. Explore everything before deciding.</Hero>
        <Section icon={Clock} title="What's included">
          <Bullets items={[
            "Full access to all dashboards (Treasurer, Events, Communications, etc.).",
            "Member management with roles and deputies.",
            "Fundraising page, marketplace and parent portal.",
            "Up to 10MB file uploads and 30 uploads per 24 hours.",
          ]} />
        </Section>
        <Section icon={Lightbulb} title="What happens at day 28">
          <Bullets items={[
            "Choose a paid plan (Basic or Standard) to keep using the admin dashboards.",
            "Locked features grey out (opacity-50) so you can still see your data, but actions are disabled.",
            "Your data is never deleted — pick up where you left off when you subscribe.",
          ]} />
        </Section>
        <Tip>Refer another PTA to earn free months — see the Referrals article.</Tip>
      </div>
    ),
  },
  "promo-codes": {
    title: "Using Promo Codes to Extend Your Trial",
    category: CATEGORY,
    readTime: "2 min read",
    content: (
      <div className="space-y-6">
        <Hero>Promo codes extend your 28-day free trial by a set number of additional days.</Hero>
        <Section icon={Tag} title="Applying a code">
          <Steps items={[
            <>Open <strong>Settings → Subscription</strong>.</>,
            <>Find the <strong>Promo Code</strong> field.</>,
            <>Enter the code exactly as supplied (case-sensitive) and click <strong>Apply</strong>.</>,
            <>Your trial expiry date updates immediately.</>,
          ]} />
        </Section>
        <Tip>Codes can only be applied during an active trial — once the trial ends, you'll need to subscribe instead.</Tip>
      </div>
    ),
  },
};
